/**
 * Вкладка «Экология» командной карточки клиента-компании (ecology-sites): те же площадки, «Проектные
 * работы» и «Отчётность», что у клиента, но по `teamEcologyApi(cid)`. Команда правит и строки,
 * выполненные Центрикой по заказу; категорию, указанную здесь, сервер помечает как указанную
 * экологом Центрики (так эколог ставит категорию по опросу, пока это не автоматизировано).
 *
 * Во вкладке «Площадки» (ecology-survey) — переход к опросу клиента по площадке и история ответов
 * «что менялось»; команда может сама отметить изменения, о которых клиент сообщил менеджеру.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { teamEcologyApi, type EcoChangeCheck, type EcoScopeApi, type EcoSite } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_UPDATE_TRIGGER_LABELS } from '@/lib/ecology'
import { useEcoData } from './use-eco-data'
import { SitesPanel } from './sites-panel'
import { ItemsSection } from './items-section'
import { EcoTrainingCard } from './training-card'
import { EcoStatusLegend } from './status-legend'
import { useEcoChangeCheck } from './change-check'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Panel } from '@/design-system/components/layout/panel'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/design-system/components/primitives/tabs'

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

/** История ответов «что менялось»; `version` — перезагрузка после нового ответа. */
function ChangeChecksHistory({
  api,
  version,
  onReport,
}: {
  api: EcoScopeApi
  version: number
  onReport: () => void
}) {
  const [checks, setChecks] = useState<EcoChangeCheck[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    api
      .listChangeChecks(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setChecks(list)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить историю'))
      })
    return () => controller.abort()
  }, [api, version])

  return (
    <Card
      className="mt-4"
      header={
        <span className="flex flex-wrap items-center justify-between gap-2">
          Что менялось на предприятии
          <Button size="sm" variant="secondary" onClick={onReport}>
            Отметить изменения
          </Button>
        </span>
      }
    >
      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : checks === null ? (
        <p className="text-13 text-txt-secondary">Загрузка…</p>
      ) : checks.length === 0 ? (
        <p className="text-13 text-txt-secondary">
          Клиент ещё не отвечал на ежегодный вопрос «что менялось».
        </p>
      ) : (
        <ul>
          {checks.map((c) => (
            <li key={c.id} className="border-b border-line py-2.5 last:border-b-0">
              <div className="font-medium text-txt">
                {c.changes.length === 0
                  ? 'Ничего не менялось'
                  : c.changes.map((ch) => ECO_UPDATE_TRIGGER_LABELS[ch]).join('; ')}
              </div>
              <div className="text-13 text-txt-secondary">
                {[
                  dateTime(c.createdAt),
                  c.accountName,
                  c.siteNames.join(', '),
                  c.changes.length > 0 ? `документов к актуализации: ${c.markedCount}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              {c.note ? <div className="mt-0.5 text-13 text-txt">{c.note}</div> : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function TeamEcologyTab({ cid }: { cid: string }) {
  const navigate = useNavigate()
  const api = useMemo(() => teamEcologyApi(cid), [cid])
  const { overview, catalog, settings, loading, error, setOverview } = useEcoData(api)
  const [checksVersion, setChecksVersion] = useState(0)
  const openSurvey = (site: EcoSite) =>
    navigate(`/team/clients/${cid}/ecology/survey?site=${site.id}`)
  const changeCheck = useEcoChangeCheck(api, overview, (next) => {
    setOverview(next)
    setChecksVersion((v) => v + 1)
  })

  if (error) return <Alert variant="danger">{error}</Alert>
  if (loading || !overview)
    return (
      <Panel>
        <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
      </Panel>
    )

  return (
    <div>
      <Tabs defaultValue="sites">
        <TabsList>
          <TabsTrigger value="sites">Площадки</TabsTrigger>
          <TabsTrigger value="documents">Проектные работы</TabsTrigger>
          <TabsTrigger value="reports">Отчётность</TabsTrigger>
          <TabsTrigger value="lab">Лабораторный контроль</TabsTrigger>
        </TabsList>
        <TabsContent value="sites">
          <SitesPanel
            api={api}
            overview={overview}
            onChange={setOverview}
            onSurvey={openSurvey}
            byTeam
          />
          {changeCheck.message ? <div className="mt-4">{changeCheck.message}</div> : null}
          <ChangeChecksHistory
            api={api}
            version={checksVersion}
            onReport={changeCheck.openChanges}
          />
          {changeCheck.dialog}
        </TabsContent>
        <TabsContent value="documents">
          {/* Обучение по экологии — из журнала обучения; заказ оформляется в «Обучении». */}
          <EcoTrainingCard training={overview.training} journalHref="/team/training" />
          <ItemsSection
            section="projects"
            api={api}
            overview={overview}
            catalog={catalog}
            byTeam
            onChange={setOverview}
            onSurvey={openSurvey}
            onOpenOrder={(orderId) => navigate(`/team/orders/${orderId}`)}
          />
        </TabsContent>
        {(['reports', 'lab'] as const).map((section) => (
          <TabsContent key={section} value={section}>
            <ItemsSection
              section={section}
              api={api}
              overview={overview}
              catalog={catalog}
              byTeam
              onChange={setOverview}
              onSurvey={openSurvey}
              onOpenOrder={(orderId) => navigate(`/team/orders/${orderId}`)}
            />
          </TabsContent>
        ))}
      </Tabs>
      <EcoStatusLegend settings={settings} />
    </div>
  )
}
